import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { colors } from "@/theme/colors";

// コース詳細画面の「空席確認」押下後にモーダルで示す状態。
// unavailable は正常応答の「空席なし」、error は Bot 操作の失敗・判定不能。
export type CourseAvailabilityStatus = "checking" | "available" | "unavailable" | "error";

type Props = {
  open: boolean;
  status: CourseAvailabilityStatus;
  onProceed: () => void;
  onClose: () => void;
};

/**
 * 「空席確認」押下時の確認結果モーダル (JPTP_CANSEI-1658)。
 *
 * 空席確認は予約サイト操作 Bot の同期 Invoke で時間がかかるため、確認中であることを
 * モーダルで示す。確認中は閉じる操作を無効化し、結果が出るまで待たせる。
 * 空席がある場合のみ「予約情報入力に進む」を出し、空席が無い場合は利用日・時間・人数の
 * 変更を促す。確認自体に失敗した場合は「空席なし」と区別した文言を出す。backend は Bot の
 * 失敗種別 (一時障害か、ログイン失敗のような恒久的な失敗か) を区別せず Unavailable に
 * 潰すため、待てば直るとは限らない。再試行ボタンは出さず、コンシェルジュへの問い合わせを
 * 案内する。
 */
export function AvailabilityCheckModal({ open, status, onProceed, onClose }: Props) {
  const { t } = useTranslation();
  const titleId = "availability-check-modal-title";
  const descriptionId = "availability-check-modal-description";
  const checking = status === "checking";

  const title = t(
    checking
      ? "courseDetail.availabilityCheckingTitle"
      : status === "available"
        ? "courseDetail.availabilityAvailableTitle"
        : status === "unavailable"
          ? "courseDetail.availabilityUnavailableTitle"
          : "courseDetail.availabilityErrorTitle",
  );
  const message = t(
    checking
      ? "courseDetail.availabilityCheckingMessage"
      : status === "available"
        ? "courseDetail.availabilityAvailableMessage"
        : status === "unavailable"
          ? "courseDetail.availabilityUnavailableMessage"
          : "courseDetail.availabilityErrorMessage",
  );

  return (
    <Dialog
      open={open}
      // 閉じる操作はモーダル内のボタンだけに限る。× ボタンは置かず、モーダル外クリックと
      // Escape でも閉じない (onClose を渡さない)。確認結果に対する行動を必ず選ばせるため。
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      slotProps={{ paper: { sx: { p: 2, width: "100%", maxWidth: 420 } } }}
    >
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>
        <Typography
          id={descriptionId}
          role={checking ? "status" : undefined}
          sx={{ color: colors.textBody, fontSize: 16, whiteSpace: "pre-line" }}
        >
          {message}
        </Typography>
        {checking && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <CircularProgress size={32} aria-hidden />
          </Box>
        )}
      </DialogContent>
      {!checking && (
        <DialogActions disableSpacing sx={{ flexDirection: "column", gap: 1, px: 3, pb: 1 }}>
          {status === "available" && (
            <Button variant="contained" fullWidth onClick={onProceed}>
              {t("courseDetail.proceedToBooking")}
            </Button>
          )}
          <Button variant="outlined" fullWidth onClick={onClose}>
            {t(
              status === "error" ? "courseDetail.availabilityClose" : "courseDetail.changeDateTime",
            )}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
